import React, { useState } from 'react';
import { Box, Button, Chip, Divider, Tooltip, Typography } from '@mui/material';
import MailOutlineIcon from '@mui/icons-material/MailOutline';
import { styled } from '@mui/material/styles';
import { colors } from 'styles/colors';
import { fontStyles } from 'styles/font-styles';
import { useTranslation } from 'react-i18next';
import { GaEvents, GaEventCategory } from 'utils/gaEvents';

export interface PerkCardProps {
  title: string;
  cta_url: string;
  id?: string;
  description?: string;
  discount_text?: string;
  note?: string;
  code?: string;
  email_code_url?: string;
  cta_label?: string;
  learn_more_url?: string;
  badge?: string;
  logo?: string;
  active?: boolean;
}

const CardContainer = styled(Box)({
  border: `1px solid ${colors.background.light.b4}`,
  borderRadius: '8px',
  backgroundColor: colors.main.white,
  boxShadow: '2px 1px 15px rgba(44, 59, 70, 0.2)',
  transition: 'box-shadow 0.4s ease-in-out',
  '&:hover': {
    boxShadow: '18px 18px 10px rgb(44 59 70 / 20%)',
  },
});

const CtaButton = styled(Button)(({ theme }) => ({
  height: '48px',
  paddingLeft: '32px',
  paddingRight: '32px',
  backgroundColor: theme.colors.main.purple,
  ...theme.fontStyles.subtext,
  color: theme.colors.main.white,
  textTransform: 'unset',
  borderRadius: '24px',
  '&:hover': {
    backgroundColor: theme.colors.main.purpleDark,
  },
}));

const CodeBox = styled(Box)({
  display: 'inline-flex',
  alignItems: 'center',
  padding: '8px 16px',
  border: `2px dashed ${colors.background.light.b4}`,
  borderRadius: '6px',
  cursor: 'pointer',
});

const PerkCard: React.FC<PerkCardProps> = ({
  title,
  cta_url,
  id,
  description,
  discount_text,
  note,
  code,
  email_code_url,
  cta_label = 'Learn more',
  learn_more_url,
  badge,
  logo,
  active = true,
}) => {
  const { t } = useTranslation();
  const [copied, setCopied] = useState(false);

  if (!active) return null;

  const handleCtaClick = () => {
    GaEvents[GaEventCategory.PERKS]?.clickedStartCampaign?.();
    window.open(cta_url, '_blank', 'noopener,noreferrer');
  };

  const handleCopyCode = () => {
    if (!code) return;
    navigator.clipboard.writeText(code);
    setCopied(true);
    GaEvents[GaEventCategory.PERKS]?.copiedCode?.();
    setTimeout(() => setCopied(false), 2000);
  };

  const handleEmailForCode = () => {
    if (!email_code_url) return;
    GaEvents[GaEventCategory.PERKS]?.copiedCode?.();
    window.location.href = email_code_url;
  };

  const handleLearnMore = () => {
    GaEvents[GaEventCategory.PERKS]?.clickedLearnMore?.();
    if (learn_more_url) {
      window.open(learn_more_url, '_blank', 'noopener,noreferrer');
    }
  };

  return (
    <CardContainer>
      {/* Header */}
      <Box display="flex" alignItems="center" padding={3}>
        {logo && (
          <Box
            component="img"
            src={logo}
            alt={title}
            sx={{
              width: 64,
              height: 64,
              borderRadius: '50%',
              objectFit: 'cover',
              marginRight: 2,
              flexShrink: 0,
            }}
          />
        )}
        <Box flex={1} minWidth={0}>
          <Typography
            sx={{
              ...fontStyles.h3,
              fontWeight: 'bold',
              color: colors.text.light.title,
              fontSize: { xs: '24px', md: '32px' },
              lineHeight: { xs: '26px', md: '34px' },
            }}
          >
            {title}
          </Typography>
          {description && (
            <Typography
              sx={{
                ...fontStyles.subtext,
                color: colors.text.light.label,
                marginTop: '8px',
                fontSize: { xs: '14px', md: '18px' },
                lineHeight: { xs: '16px', md: '20px' },
              }}
            >
              {description}
            </Typography>
          )}
        </Box>
        {badge && (
          <Chip
            label={badge}
            size="small"
            sx={{
              flexShrink: 0,
              marginLeft: 2,
              alignSelf: 'flex-start',
              backgroundColor: colors.main.yellow,
              color: colors.main.white,
              fontWeight: 'bold',
              fontSize: { xs: '11px', md: '14px' },
              height: { xs: '24px', md: '32px' },
              padding: { xs: '0 4px', md: '0 12px' },
            }}
          />
        )}
      </Box>

      <Divider sx={{ borderColor: colors.background.light.b3 }} />

      {/* Body */}
      <Box padding={3}>
        {discount_text && (
          <Typography
            sx={{ ...fontStyles.h4, color: colors.text.light.title, marginBottom: note ? 0.5 : 2 }}
          >
            {discount_text}
          </Typography>
        )}
        {note && (
          <Typography
            sx={{ ...fontStyles.subtext, color: colors.text.light.label, marginBottom: 2 }}
          >
            {note}
          </Typography>
        )}
        <Box
          display="flex"
          alignItems="center"
          justifyContent="space-between"
          flexWrap="wrap"
          gap={2}
        >
          {code && (
            <Tooltip title={copied ? t('Copied!') : t('Click to copy')} arrow placement="top">
              <CodeBox onClick={handleCopyCode}>
                <Typography
                  sx={{ ...fontStyles.tiny, color: colors.text.light.label, marginRight: '8px' }}
                >
                  {t('Code')}:
                </Typography>
                <Typography
                  sx={{ ...fontStyles.subtext, fontWeight: 'bold', color: colors.text.light.title }}
                >
                  {code}
                </Typography>
              </CodeBox>
            </Tooltip>
          )}
          {!code && email_code_url && (
            <CodeBox onClick={handleEmailForCode}>
              <Typography
                sx={{ ...fontStyles.subtext, fontWeight: 'bold', color: colors.text.light.title }}
              >
                {t('Email for code')}
              </Typography>
              <MailOutlineIcon
                sx={{ fontSize: 16, color: colors.text.light.label, marginLeft: '8px' }}
              />
            </CodeBox>
          )}
          {!learn_more_url && (
            <CtaButton variant="contained" onClick={handleCtaClick}>
              {t(cta_label)}
            </CtaButton>
          )}
        </Box>
      </Box>

      {/* Footer — only shown when learn_more_url exists */}
      {learn_more_url && (
        <Box
          display="flex"
          alignItems="center"
          justifyContent="space-between"
          padding={3}
          paddingTop={0}
        >
          <Typography
            component="a"
            onClick={handleLearnMore}
            sx={{
              ...fontStyles.subtext,
              color: colors.text.light.label,
              cursor: 'pointer',
              textDecoration: 'none',
              '&:hover': { textDecoration: 'underline' },
            }}
          >
            {t('Learn more')}
          </Typography>
          <CtaButton variant="contained" onClick={handleCtaClick}>
            {t(cta_label)}
          </CtaButton>
        </Box>
      )}
    </CardContainer>
  );
};

export default PerkCard;
